import { useState, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

type TagInputProps = {
  tags: string[]
  onChange: (tags: string[]) => void
  suggestions: string[]
}

export default function TagInput({ tags, onChange, suggestions }: TagInputProps) {
  const [input, setInput] = useState('')

  const addTag = (tag: string) => {
    const normalized = tag.trim().toLowerCase()
    if (!normalized || tags.includes(normalized)) return
    onChange([...tags, normalized])
    setInput('')
  }

  const removeTag = (tag: string) => onChange(tags.filter((t) => t !== tag))

  const toggleSuggestion = (tag: string) => {
    if (tags.includes(tag)) removeTag(tag)
    else onChange([...tags, tag])
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  // suggestions render as toggles; free-typed tags render as removable chips
  const customTags = tags.filter((t) => !suggestions.includes(t))

  return (
    <div className="space-y-4">
      {suggestions.length > 0 && (
        <div>
          <p className="text-sm text-muted-foreground mb-2">Suggested tags</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => {
              const selected = tags.includes(s)
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSuggestion(s)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                    selected
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border text-muted-foreground hover:border-foreground/30'
                  )}
                >
                  {s}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <p className="text-sm text-muted-foreground mb-2">Add your own</p>
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl border border-input bg-card min-h-11 transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
          {customTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 bg-muted text-foreground text-sm px-2.5 py-1 rounded-full"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={customTags.length === 0 ? 'Type a tag and press Enter' : ''}
            className="flex-1 min-w-30 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
        </div>
      </div>
    </div>
  )
}
